/**
 * Attachment Service — unified file/photo attachments across jobs, deals,
 * customers, and customer_services.
 *
 * Faults use a sibling service (fault-attachment.service.ts) which delegates
 * propagation to this module. The shape stored on Fault documents is
 * FaultAttachment (has `stage`); everywhere else stores Attachment (no stage).
 *
 * Propagation contract
 * --------------------
 * The caller explicitly tells the service where to write the attachment.
 * Typical propagation paths:
 *
 *   Job upload        → job + deal + fault + customer
 *   Deal upload       → deal + customer
 *   Fault upload      → fault + customer + customer_service
 *   Customer upload   → customer (top-level only)
 *   Service upload    → customer_service + customer
 *
 * The Storage file is uploaded ONCE. Each entity doc gets the same `id`
 * (so delete can match across entities) but may have slightly different
 * shapes (e.g. fault copy carries `stage`). For deletion, the caller
 * passes the same propagation refs — we read each doc and filter by `id`.
 */

import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import {
  doc,
  getDoc,
  updateDoc,
  arrayUnion,
} from 'firebase/firestore';
import { db, storage } from '@/firebase';
import { getTenantIdSync } from '@/lib/tenant';
import { compressImage, generateThumbnail } from './photo.service';
import type {
  Attachment,
  AttachmentEntityType,
  AttachmentSource,
} from '@/types/attachment.types';

// ============================================================================
// Constants
// ============================================================================

const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25 MB — images + arbitrary files
const MAX_VIDEO_SIZE = 200 * 1024 * 1024; // 200 MB — videos (uploaded as-is, no compression)
const COMPRESS_THRESHOLD = 2 * 1024 * 1024; // Compress images > 2 MB

// Map entity type → Firestore collection name.
const ENTITY_COLLECTIONS: Record<AttachmentEntityType, string> = {
  job: 'jobs',
  deal: 'deals',
  fault: 'faults',
  customer: 'customers',
  customer_service: 'customer_services',
};

// ============================================================================
// Helpers
// ============================================================================

function sanitizeFilename(name: string): string {
  return name
    .replace(/[^a-zA-Z0-9._-]/g, '_')
    .replace(/_+/g, '_')
    .substring(0, 100);
}

function buildStoragePath(
  tenantId: string,
  entityType: AttachmentEntityType,
  entityId: string,
  filename: string,
): string {
  const ts = Date.now();
  const safe = sanitizeFilename(filename || 'file');
  return `attachments/${tenantId}/${entityType}/${entityId}/${ts}_${safe}`;
}

/**
 * Strip undefined fields so Firestore arrayUnion doesn't choke.
 */
function cleanAttachment(a: Attachment): Attachment {
  const out: Attachment = {
    id: a.id,
    kind: a.kind,
    timestamp: a.timestamp,
    addedBy: a.addedBy,
  };
  if (a.addedByName) out.addedByName = a.addedByName;
  if (a.fileName) out.fileName = a.fileName;
  if (a.fileSize != null) out.fileSize = a.fileSize;
  if (a.mimeType) out.mimeType = a.mimeType;
  if (a.url) out.url = a.url;
  if (a.storagePath) out.storagePath = a.storagePath;
  if (a.thumbnailUrl) out.thumbnailUrl = a.thumbnailUrl;
  if (a.emailDirection) out.emailDirection = a.emailDirection;
  if (a.emailSubject) out.emailSubject = a.emailSubject;
  if (a.emailFrom) out.emailFrom = a.emailFrom;
  if (a.emailTo) out.emailTo = a.emailTo;
  if (a.emailCc) out.emailCc = a.emailCc;
  if (a.emailBcc) out.emailBcc = a.emailBcc;
  if (a.emailDate) out.emailDate = a.emailDate;
  if (a.emailMessageId) out.emailMessageId = a.emailMessageId;
  if (a.emailBody) out.emailBody = a.emailBody;
  if (a.emailHtmlBody) out.emailHtmlBody = a.emailHtmlBody;
  if (a.emailSummary) out.emailSummary = a.emailSummary;
  if (a.emailAttachments && a.emailAttachments.length > 0) {
    out.emailAttachments = a.emailAttachments.map((ea) => ({
      id: ea.id,
      fileName: ea.fileName,
      mimeType: ea.mimeType,
      fileSize: ea.fileSize,
      url: ea.url,
      storagePath: ea.storagePath,
    }));
  }
  if (a.caption) out.caption = a.caption;
  if (a.source) {
    out.source = {
      type: a.source.type,
      id: a.source.id,
    };
    if (a.source.label) out.source.label = a.source.label;
  }
  return out;
}

// ============================================================================
// Types
// ============================================================================

/** Where to also write the attachment, in addition to the source entity */
export interface PropagateRefs {
  customerId?: string;
  dealId?: string;
  faultId?: string;
  serviceId?: string; // customer_services/{id}
  jobId?: string; // rarely used — included for symmetry
  /**
   * service_requests/{id} — handled OUTSIDE buildTargets because service
   * requests use their own ServiceRequestAttachment schema (category /
   * uploadedAt / uploadedByName) rather than the shared Attachment shape.
   * Upload paths write a MAPPED copy with the same id; delete sweeps it by id.
   */
  serviceRequestId?: string;
}

export interface UploadOptions {
  file: File;
  /** Where the user is uploading from — becomes Attachment.source */
  source: AttachmentSource;
  /** Additional entities to sync the attachment to */
  propagate?: PropagateRefs;
  addedBy: string;
  addedByName?: string;
  caption?: string;
}

// ============================================================================
// Propagation helpers
// ============================================================================

/** Get the list of (collection, id) pairs to write/remove the attachment from. */
function buildTargets(
  source: AttachmentSource,
  propagate: PropagateRefs | undefined,
): { collection: string; id: string }[] {
  const targets: { collection: string; id: string }[] = [];
  const seen = new Set<string>();

  const push = (collection: string, id: string | undefined | null) => {
    if (!id) return;
    const key = `${collection}:${id}`;
    if (seen.has(key)) return;
    seen.add(key);
    targets.push({ collection, id });
  };

  // Source entity first — that's the authoritative copy
  push(ENTITY_COLLECTIONS[source.type], source.id);

  if (propagate) {
    push(ENTITY_COLLECTIONS.job, propagate.jobId);
    push(ENTITY_COLLECTIONS.deal, propagate.dealId);
    push(ENTITY_COLLECTIONS.fault, propagate.faultId);
    push(ENTITY_COLLECTIONS.customer_service, propagate.serviceId);
    push(ENTITY_COLLECTIONS.customer, propagate.customerId);
  }

  return targets;
}

/**
 * Write a MAPPED copy of the attachment onto a linked service request.
 * Service requests carry `ServiceRequestAttachment[]` (their own schema with
 * category/uploadedAt/uploadedByName), so the generic propagation can't be
 * reused — this maps field-for-field and keeps the SAME id so deleteAttachment
 * can sweep the copy later. Best-effort: never throws.
 */
async function propagateToServiceRequest(
  serviceRequestId: string | undefined,
  attachment: Attachment,
): Promise<void> {
  if (!serviceRequestId || !attachment.url) return;
  const mime = attachment.mimeType || '';
  const category =
    attachment.kind === 'photo' ? 'image'
    : mime.includes('pdf') ? 'pdf'
    : mime.includes('sheet') || mime.includes('csv') || mime.includes('excel') ? 'spreadsheet'
    : attachment.kind === 'file' ? 'document'
    : 'other';
  const mapped: Record<string, unknown> = {
    id: attachment.id,
    url: attachment.url,
    storagePath: attachment.storagePath || '',
    fileName: attachment.fileName || 'file',
    fileType: mime || 'application/octet-stream',
    fileSize: attachment.fileSize || 0,
    category,
    uploadedAt: attachment.timestamp,
    uploadedBy: attachment.addedBy,
    uploadedByName: attachment.addedByName || attachment.addedBy,
  };
  if (attachment.caption) mapped.caption = attachment.caption;
  try {
    await updateDoc(doc(db, 'service_requests', serviceRequestId), {
      attachments: arrayUnion(mapped),
      updatedAt: new Date().toISOString(),
    });
  } catch (err) {
    console.warn(
      `[attachment] Failed to propagate to service_requests/${serviceRequestId}:`,
      err,
    );
  }
}

/**
 * Remove the mapped copy from a linked service request by attachment id.
 * Mirrors the read-filter-write pattern deleteAttachment uses for the
 * shared-schema targets. Best-effort: never throws.
 */
async function removeFromServiceRequest(
  serviceRequestId: string | undefined,
  attachmentId: string,
): Promise<void> {
  if (!serviceRequestId) return;
  try {
    const docRef = doc(db, 'service_requests', serviceRequestId);
    const snap = await getDoc(docRef);
    if (!snap.exists()) return;
    const current = (snap.data().attachments || []) as { id?: string }[];
    const filtered = current.filter((a) => a.id !== attachmentId);
    if (filtered.length === current.length) return;
    await updateDoc(docRef, {
      attachments: filtered,
      updatedAt: new Date().toISOString(),
    });
  } catch (err) {
    console.warn(
      `[attachment] Failed to remove from service_requests/${serviceRequestId}:`,
      err,
    );
  }
}

/** Write the attachment object to each target entity's attachments[] array */
async function propagateAttachment(
  targets: { collection: string; id: string }[],
  attachment: Attachment,
): Promise<void> {
  const clean = cleanAttachment(attachment);
  await Promise.all(
    targets.map((t) =>
      updateDoc(doc(db, t.collection, t.id), {
        attachments: arrayUnion(clean),
        updatedAt: new Date().toISOString(),
      }).catch((err) => {
        // Don't let one missing doc break the whole propagation —
        // log and continue. Source write already succeeded.
        console.warn(
          `[attachment] Failed to propagate to ${t.collection}/${t.id}:`,
          err,
        );
      }),
    ),
  );
}

// ============================================================================
// Upload API
// ============================================================================

/** Upload a photo and write it (with propagation) to Firestore. */
export async function uploadAttachmentPhoto(
  opts: UploadOptions,
): Promise<Attachment> {
  const { file, source, propagate, addedBy, addedByName, caption } = opts;

  if (!file.type.startsWith('image/')) {
    throw new Error('File must be an image');
  }
  if (file.size > MAX_FILE_SIZE) {
    throw new Error(
      `File too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max 25 MB.`,
    );
  }

  const tenantId = getTenantIdSync();
  const fileName = file.name || 'photo.jpg';

  // Compress if over threshold — falls back to original on failure
  let uploadBlob: Blob = file;
  if (file.size > COMPRESS_THRESHOLD) {
    try {
      uploadBlob = await compressImage(file);
    } catch {
      uploadBlob = file;
    }
  }

  const storagePath = buildStoragePath(tenantId, source.type, source.id, fileName);
  const storageRef = ref(storage, storagePath);
  await uploadBytes(storageRef, uploadBlob, {
    contentType: 'image/jpeg',
    customMetadata: {
      tenantId,
      entityType: source.type,
      entityId: source.id,
      addedBy,
      uploadedAt: new Date().toISOString(),
    },
  });
  const url = await getDownloadURL(storageRef);

  // Thumbnail — non-critical, best effort
  let thumbnailUrl: string | undefined;
  try {
    const thumbBlob = await generateThumbnail(file);
    const thumbPath = storagePath.replace(/(\.[^.]+)?$/, '_thumb$1');
    const thumbRef = ref(storage, thumbPath);
    await uploadBytes(thumbRef, thumbBlob, { contentType: 'image/jpeg' });
    thumbnailUrl = await getDownloadURL(thumbRef);
  } catch {
    // ignore
  }

  const attachment: Attachment = {
    id: crypto.randomUUID(),
    kind: 'photo',
    timestamp: new Date().toISOString(),
    addedBy,
    fileName,
    fileSize: file.size,
    mimeType: file.type || 'image/jpeg',
    url,
    storagePath,
    source,
  };
  if (addedByName) attachment.addedByName = addedByName;
  if (caption) attachment.caption = caption;
  if (thumbnailUrl) attachment.thumbnailUrl = thumbnailUrl;

  await propagateAttachment(buildTargets(source, propagate), attachment);
  await propagateToServiceRequest(propagate?.serviceRequestId, attachment);
  return attachment;
}

/**
 * Upload a video (kind='video') and write it with propagation.
 *
 * Videos are stored as-is — no client-side transcoding/compression and no
 * generated poster thumbnail (reliable browser-side video posters need a
 * canvas seek dance that's fragile across codecs; the grid renders a live
 * <video> preview instead). A larger size cap applies than for images/files.
 */
export async function uploadAttachmentVideo(
  opts: UploadOptions,
): Promise<Attachment> {
  const { file, source, propagate, addedBy, addedByName, caption } = opts;

  if (!file.type.startsWith('video/')) {
    throw new Error('File must be a video');
  }
  if (file.size > MAX_VIDEO_SIZE) {
    throw new Error(
      `Video too large (${(file.size / 1024 / 1024).toFixed(0)} MB). Max 200 MB.`,
    );
  }

  const tenantId = getTenantIdSync();
  const fileName = file.name || 'video.mp4';
  const storagePath = buildStoragePath(tenantId, source.type, source.id, fileName);
  const storageRef = ref(storage, storagePath);

  await uploadBytes(storageRef, file, {
    contentType: file.type || 'video/mp4',
    customMetadata: {
      tenantId,
      entityType: source.type,
      entityId: source.id,
      addedBy,
      uploadedAt: new Date().toISOString(),
    },
  });
  const url = await getDownloadURL(storageRef);

  const attachment: Attachment = {
    id: crypto.randomUUID(),
    kind: 'video',
    timestamp: new Date().toISOString(),
    addedBy,
    fileName,
    fileSize: file.size,
    mimeType: file.type || 'video/mp4',
    url,
    storagePath,
    source,
  };
  if (addedByName) attachment.addedByName = addedByName;
  if (caption) attachment.caption = caption;

  await propagateAttachment(buildTargets(source, propagate), attachment);
  await propagateToServiceRequest(propagate?.serviceRequestId, attachment);
  return attachment;
}

/**
 * Upload a photo OR video, dispatching by MIME type. This is the entry point
 * the shared media picker uses so a single "Media" control can accept both.
 */
export async function uploadAttachmentMedia(
  opts: UploadOptions,
): Promise<Attachment> {
  if (opts.file.type.startsWith('video/')) {
    return uploadAttachmentVideo(opts);
  }
  return uploadAttachmentPhoto(opts);
}

/** Upload an arbitrary file (PDF, doc, etc.) and write it with propagation. */
export async function uploadAttachmentFile(
  opts: UploadOptions,
): Promise<Attachment> {
  const { file, source, propagate, addedBy, addedByName, caption } = opts;

  if (file.size > MAX_FILE_SIZE) {
    throw new Error(
      `File too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max 25 MB.`,
    );
  }

  const tenantId = getTenantIdSync();
  const fileName = file.name || 'file';
  const storagePath = buildStoragePath(tenantId, source.type, source.id, fileName);
  const storageRef = ref(storage, storagePath);

  await uploadBytes(storageRef, file, {
    contentType: file.type || 'application/octet-stream',
    customMetadata: {
      tenantId,
      entityType: source.type,
      entityId: source.id,
      addedBy,
      uploadedAt: new Date().toISOString(),
    },
  });
  const url = await getDownloadURL(storageRef);

  const attachment: Attachment = {
    id: crypto.randomUUID(),
    kind: 'file',
    timestamp: new Date().toISOString(),
    addedBy,
    fileName,
    fileSize: file.size,
    mimeType: file.type || 'application/octet-stream',
    url,
    storagePath,
    source,
  };
  if (addedByName) attachment.addedByName = addedByName;
  if (caption) attachment.caption = caption;

  await propagateAttachment(buildTargets(source, propagate), attachment);
  await propagateToServiceRequest(propagate?.serviceRequestId, attachment);
  return attachment;
}

// ============================================================================
// Email upload — drag-dropped .eml files
// ============================================================================

export interface UploadEmailOptions {
  /** The .eml file the user dropped. */
  emlFile: File;
  /** Pre-parsed email payload (caller already ran parseEmlFile for the preview). */
  parsed: import('./eml-parser.service').ParsedEmail;
  /** Where the user is uploading from — becomes Attachment.source */
  source: AttachmentSource;
  /** Additional entities to sync the attachment to */
  propagate?: PropagateRefs;
  addedBy: string;
  addedByName?: string;
  /** 'inbound' (we received it) | 'outbound' (we sent it). Defaults to inbound. */
  direction?: 'inbound' | 'outbound';
  /** Optional progress callback (0-100). Fires for the .eml upload only. */
  onProgress?: (percent: number) => void;
}

/**
 * Upload a parsed .eml file + every nested attachment, then write a single
 * Attachment record (kind='email') back to the source entity + propagation
 * targets.
 *
 * Storage layout:
 *   attachments/{tenantId}/{entityType}/{entityId}/email-{ts}_{safeSubject}.eml
 *   attachments/{tenantId}/{entityType}/{entityId}/email-{ts}/{nestedFilename}
 *
 * The nested files live under a sibling folder named after the parent .eml's
 * timestamp so a future "delete email" can sweep the whole folder in one go.
 */
export async function uploadAttachmentEmail(
  opts: UploadEmailOptions,
): Promise<Attachment> {
  const { emlFile, parsed, source, propagate, addedBy, addedByName } = opts;
  const direction = opts.direction || 'inbound';

  if (emlFile.size > MAX_FILE_SIZE) {
    throw new Error(
      `Email file too large (${(emlFile.size / 1024 / 1024).toFixed(1)} MB). Max 25 MB.`,
    );
  }

  const tenantId = getTenantIdSync();
  const ts = Date.now();
  const subjectSlug = sanitizeFilename(parsed.subject || 'email').slice(0, 60);
  const emlFileName = `email-${ts}_${subjectSlug}.eml`;
  const emlStoragePath = `attachments/${tenantId}/${source.type}/${source.id}/${emlFileName}`;

  // 1. Upload the original .eml as the archive copy.
  const emlRef = ref(storage, emlStoragePath);
  await uploadBytes(emlRef, emlFile, {
    contentType: 'message/rfc822',
    customMetadata: {
      tenantId,
      entityType: source.type,
      entityId: source.id,
      addedBy,
      uploadedAt: new Date().toISOString(),
      emailSubject: parsed.subject.slice(0, 200),
      emailFrom: parsed.from.slice(0, 200),
    },
  });
  const emlUrl = await getDownloadURL(emlRef);
  opts.onProgress?.(100);

  // 2. Upload nested attachments in parallel. A failure on a single nested
  //    file shouldn't tank the whole email attach — log and skip.
  const nestedFolder = `attachments/${tenantId}/${source.type}/${source.id}/email-${ts}`;
  const nestedUploads = await Promise.all(
    parsed.attachments.map(async (att) => {
      try {
        const safeName = sanitizeFilename(att.filename || 'attachment');
        const nestedPath = `${nestedFolder}/${safeName}`;
        const nestedRef = ref(storage, nestedPath);
        await uploadBytes(nestedRef, att.content, {
          contentType: att.mimeType,
          customMetadata: {
            tenantId,
            entityType: source.type,
            entityId: source.id,
            addedBy,
            uploadedAt: new Date().toISOString(),
            parentEmail: emlFileName,
          },
        });
        const url = await getDownloadURL(nestedRef);
        return {
          id: crypto.randomUUID(),
          fileName: att.filename,
          mimeType: att.mimeType,
          fileSize: att.size,
          url,
          storagePath: nestedPath,
        };
      } catch (err) {
        console.warn(
          `[attachment] Failed to upload nested email attachment ${att.filename}:`,
          err,
        );
        return null;
      }
    }),
  );
  const nested = nestedUploads.filter((x): x is NonNullable<typeof x> => x !== null);

  // 3. Build the Attachment record. Everything optional that's blank gets
  //    stripped by cleanAttachment before it hits Firestore.
  const attachment: Attachment = {
    id: crypto.randomUUID(),
    kind: 'email',
    timestamp: new Date().toISOString(),
    addedBy,
    fileName: emlFileName,
    fileSize: emlFile.size,
    mimeType: 'message/rfc822',
    url: emlUrl,
    storagePath: emlStoragePath,
    emailDirection: direction,
    emailSubject: parsed.subject,
    emailFrom: parsed.from,
    emailTo: parsed.to,
    emailCc: parsed.cc || undefined,
    emailBcc: parsed.bcc || undefined,
    emailDate: parsed.date,
    emailMessageId: parsed.messageId || undefined,
    emailBody: parsed.text,
    emailHtmlBody: parsed.html || undefined,
    emailSummary: parsed.summary,
    emailAttachments: nested.length > 0 ? nested : undefined,
    source,
  };
  if (addedByName) attachment.addedByName = addedByName;

  await propagateAttachment(buildTargets(source, propagate), attachment);
  await propagateToServiceRequest(propagate?.serviceRequestId, attachment);
  return attachment;
}

// ============================================================================
// Delete API
// ============================================================================

export interface DeleteOptions {
  attachmentId: string;
  /** Where the attachment currently lives — must cover all entities it was written to */
  source: AttachmentSource;
  propagate?: PropagateRefs;
  /** Storage path of the file — optional, used to delete the underlying file */
  storagePath?: string;
  /** Thumbnail storage path (derived if not provided) */
  thumbnailStoragePath?: string;
}

/**
 * Resolve the propagation refs for a given source entity by reading its
 * parent/linked foreign keys. Used at delete time when the caller doesn't
 * know the full chain (e.g. deleting from an aggregated customer view).
 */
async function resolveLinkedFromSource(
  source: AttachmentSource,
): Promise<PropagateRefs> {
  const refs: PropagateRefs = {};
  try {
    const collection = ENTITY_COLLECTIONS[source.type];
    const snap = await getDoc(doc(db, collection, source.id));
    if (!snap.exists()) return refs;
    const data = snap.data() as {
      customerId?: string;
      dealId?: string;
      faultId?: string;
      serviceId?: string;
      serviceRequestId?: string;
    };
    if (source.type === 'job') {
      if (data.customerId) refs.customerId = data.customerId;
      if (data.dealId) refs.dealId = data.dealId;
      if (data.faultId) refs.faultId = data.faultId;
      if (data.serviceRequestId) refs.serviceRequestId = data.serviceRequestId;
    } else if (source.type === 'deal') {
      if (data.customerId) refs.customerId = data.customerId;
    } else if (source.type === 'fault') {
      if (data.customerId) refs.customerId = data.customerId;
      if (data.serviceId) refs.serviceId = data.serviceId;
    } else if (source.type === 'customer_service') {
      if (data.customerId) refs.customerId = data.customerId;
    }
    // 'customer' has no upstream — no additional refs
  } catch (err) {
    console.warn('[attachment] Failed to resolve linked refs from source:', err);
  }
  return refs;
}

/**
 * Remove an attachment from source + all propagated entities and delete the
 * underlying Storage file.
 *
 * If `propagate` isn't supplied, we re-discover the propagation chain from
 * the source document. This matters for deletes initiated from aggregated
 * views (e.g. deleting a fault-sourced attachment from the customer Files
 * tab) where the caller can't know the original propagation targets.
 */
export async function deleteAttachment(opts: DeleteOptions): Promise<void> {
  const { attachmentId, source, storagePath } = opts;
  const propagate =
    opts.propagate ?? (await resolveLinkedFromSource(source));
  const targets = buildTargets(source, propagate);

  // Remove from each entity — read, filter, write. We can't use arrayRemove
  // because the object may have been stored with minor shape differences.
  await Promise.all(
    targets.map(async (t) => {
      try {
        const docRef = doc(db, t.collection, t.id);
        const snap = await getDoc(docRef);
        if (!snap.exists()) return;
        const data = snap.data() as { attachments?: Attachment[] };
        const current = data.attachments || [];
        if (!current.length) return;
        const filtered = current.filter((a) => a.id !== attachmentId);
        if (filtered.length === current.length) return; // nothing to remove
        await updateDoc(docRef, {
          attachments: filtered,
          updatedAt: new Date().toISOString(),
        });
      } catch (err) {
        console.warn(
          `[attachment] Failed to remove from ${t.collection}/${t.id}:`,
          err,
        );
      }
    }),
  );

  // Service-request copy lives in its own schema — sweep it by id too.
  await removeFromServiceRequest(propagate?.serviceRequestId, attachmentId);

  // Delete storage file + thumbnail (best effort)
  if (storagePath) {
    try {
      await deleteObject(ref(storage, storagePath));
    } catch (err) {
      console.warn('[attachment] Storage delete failed (continuing):', err);
    }
    const thumbPath =
      opts.thumbnailStoragePath ||
      storagePath.replace(/(\.[^.]+)?$/, '_thumb$1');
    try {
      await deleteObject(ref(storage, thumbPath));
    } catch {
      // ignore — thumbnail may not exist
    }
  }
}

// ============================================================================
// Read helpers
// ============================================================================

/** Safely pull attachments from any entity-shaped object */
export function getAttachments(
  entity: { attachments?: Attachment[] } | null | undefined,
): Attachment[] {
  return entity?.attachments ?? [];
}

/** Format a file size for display: 1.2 MB / 340 KB / 120 B */
export function formatFileSize(bytes: number | undefined): string {
  if (!bytes || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * Convenience: build propagation refs from a Job document.
 * Covers the common case of uploading FROM a job — syncs to deal, fault, customer.
 */
export function propagateFromJob(job: {
  id?: string;
  dealId?: string;
  faultId?: string;
  customerId?: string | null;
  serviceRequestId?: string;
}): PropagateRefs {
  const p: PropagateRefs = {};
  if (job.dealId) p.dealId = job.dealId;
  if (job.faultId) p.faultId = job.faultId;
  if (job.customerId) p.customerId = job.customerId;
  if (job.serviceRequestId) p.serviceRequestId = job.serviceRequestId;
  return p;
}

/** Build propagation refs from a Deal document (syncs to customer). */
export function propagateFromDeal(deal: {
  customerId?: string | null;
}): PropagateRefs {
  const p: PropagateRefs = {};
  if (deal.customerId) p.customerId = deal.customerId;
  return p;
}

/** Build propagation refs from a Fault document (syncs to customer + service). */
export function propagateFromFault(fault: {
  customerId?: string;
  serviceId?: string;
}): PropagateRefs {
  const p: PropagateRefs = {};
  if (fault.customerId) p.customerId = fault.customerId;
  if (fault.serviceId) p.serviceId = fault.serviceId;
  return p;
}

/** Build propagation refs from a CustomerService document (syncs to customer). */
export function propagateFromService(svc: {
  customerId?: string;
}): PropagateRefs {
  const p: PropagateRefs = {};
  if (svc.customerId) p.customerId = svc.customerId;
  return p;
}
